/**
 * threadClosure — "is this thread closed to me, and what may I say about it?"
 * as ONE answer (2026-09-21, owner directive).
 *
 * THE OWNER'S ASK. "when a group thread or any thread is archived no individuals
 * within the group should be able to see the group thread anymore as it is
 * gracefully taken away, even on the mentions or threads or direct tabs … if a
 * user is mentioned in a group thread that mention summary in the mentions
 * section should be greyed out, user can find out the group thread was archived
 * when hovering over it."
 *
 * The client twin of functions/src/prest0nSession/threadArchiveState.ts, which
 * says the same thing about the same fact for the server's writers. Four
 * surfaces need this answer — the mentions tab and the bell's three renderers —
 * and before this module there was no shared owner, which is exactly why they
 * had drifted apart: the threads and direct tabs honour the archive, the
 * mentions tab and the bell did not.
 *
 * ONE FACT DECIDES THE ACTION. `archived === true` on the viewer's OWN index
 * doc (`users/{uid}/chat_threads/{threadId}`), because every writer that closes
 * a thread to a person sets it there: the archive-for-everyone fanout
 * (threadMembershipWrite.archiveThreadForAllMembers), the removal mirror
 * (archiveThreadIndexForParticipant), the person's own archive
 * (chatThreadIndexRepository.archiveChatThread) and the janitor. The rail query
 * is `archived == false`, so an archived doc is already unreachable by every
 * existing surface whatever the cause.
 *
 * THE SHAPE DECIDES THE WORDS, and that is not the same question. Those four
 * writers stamp four different amounts of attribution, and the existing archive
 * toast already lies about one of them: it renders "a teammate archived …" for
 * `archivedBy: 'orphan_janitor'`. So the copy branches on the ATTRIBUTION SHAPE
 * this module resolves, never on the boolean, and can therefore never invent a
 * person or name a robot.
 *
 * Pure and Firebase-free on purpose: every consumer is a component or a hook,
 * and a module that reaches `firebase/auth` at import time takes down every
 * suite that renders a component importing it (the `attachmentStorage` lesson,
 * CLAUDE.md).
 */

/**
 * `archivedBy` values the SERVER writes that are MACHINES, never people
 * (functions/src/maintenance/threadJanitor.ts:261,435). A person must never be
 * shown one of these as a name, and must never be told a teammate did it.
 */
export const JANITOR_ARCHIVER_IDS: readonly string[] = ['orphan_janitor', 'stub_flood_janitor'];

/**
 * Every attribution shape the copy must answer for. A CLOSED tuple, because it
 * is the denominator of the copy's totality test: widening this union without
 * adding a case must fail the build, not ship a blank sentence.
 */
export const ARCHIVE_ATTRIBUTIONS = ['person', 'you', 'system', 'unknown'] as const;
export type ArchiveAttribution = typeof ARCHIVE_ATTRIBUTIONS[number];

/** The archive facts as they sit on the viewer's own index doc. */
export interface ThreadClosureFacts {
  archived: boolean;
  /** A uid, or a janitor id — never trusted as a name. */
  archivedBy?: string;
  archivedByName?: string;
  /** ms. 0 ⇒ no usable stamp; the copy then omits the date entirely. */
  atMs?: number;
}

/** The resolved verdict a surface renders from. */
export interface ThreadClosure {
  attribution: ArchiveAttribution;
  /** A real person's display name, or ''. NEVER a uid and never a janitor id. */
  byName: string;
  /** ms, or 0 when no usable stamp was written. */
  atMs: number;
}

/** Whether the viewer may still open a thread, before any read has answered. */
export type ThreadClosureState = 'live' | 'retired' | 'unknown';

const trim = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

/**
 * The archived-at stamp, from EITHER field name and EITHER type.
 *
 * Two writers, two spellings, two types: the membership fanout writes
 * `archivedAt` as a number of ms, the janitor writes `archivedAtTs` as a server
 * Timestamp, and nothing read either before today. A reader that knows one of
 * them prints no date for half the archives in existence; a reader that treats
 * an absent stamp as 0 and prints it anyway ships "archived on jan 1 1970".
 * Both are answered here, once, and an unusable stamp is 0 — at which the copy
 * omits the clause rather than guessing.
 */
export function readArchivedAtMs(data: Record<string, unknown> | null | undefined): number {
  if (!data) return 0;
  const direct = data.archivedAt;
  if (typeof direct === 'number' && Number.isFinite(direct) && direct > 0) return Math.floor(direct);
  const stamp = data.archivedAtTs ?? data.archivedAt;
  if (stamp && typeof stamp === 'object') {
    const asTimestamp = stamp as { toMillis?: () => number; seconds?: number; _seconds?: number };
    if (typeof asTimestamp.toMillis === 'function') {
      const ms = Number(asTimestamp.toMillis());
      if (Number.isFinite(ms) && ms > 0) return Math.floor(ms);
    }
    const seconds = typeof asTimestamp.seconds === 'number'
      ? asTimestamp.seconds
      : typeof asTimestamp._seconds === 'number' ? asTimestamp._seconds : null;
    if (seconds !== null && Number.isFinite(seconds) && seconds > 0) return Math.floor(seconds * 1000);
  }
  return 0;
}

/**
 * THE verdict. `null` ⇒ the thread is open and the row renders exactly as it did
 * before this module existed.
 *
 * THE BRANCH ORDER IS LOAD-BEARING, and each step is a thing that would
 * otherwise be a lie:
 *
 *  1. not archived ⇒ null.
 *  2. no `archivedBy` at all ⇒ `unknown`. This is the REMOVAL case
 *     (archiveThreadIndexForParticipant writes `archived` alone) and the
 *     viewer's own personal archive (archiveChatThread writes
 *     `{archived, instanceId}`). The two are indistinguishable on the client, so
 *     the copy for `unknown` says only what is true of both: it is closed to
 *     you. It deliberately does NOT claim the thread was archived, because for a
 *     removed participant it may still be live for everyone else.
 *  3. archived by the viewer ⇒ `you`.
 *  4. archived by a JANITOR ⇒ `system`, and `byName` is forced empty even when
 *     the document carried one. This branch sits BEFORE the name branch so no
 *     ordering accident can put a robot's id in front of a person, and it is
 *     why the "a teammate archived it" line cannot be reached from here.
 *  5. a real name ⇒ `person`.
 *  6. a uid with no name ⇒ `unknown`. A raw uid must never reach copy.
 */
export function resolveThreadClosure(
  facts: ThreadClosureFacts | null | undefined,
  viewerUid: string | null | undefined,
): ThreadClosure | null {
  if (facts?.archived !== true) return null;
  const atMs = Number.isFinite(facts.atMs) && (facts.atMs as number) > 0 ? Math.floor(facts.atMs as number) : 0;
  const by = trim(facts.archivedBy);
  const viewer = trim(viewerUid);
  if (!by) return { attribution: 'unknown', byName: '', atMs };
  if (viewer && by === viewer) return { attribution: 'you', byName: '', atMs };
  if (JANITOR_ARCHIVER_IDS.includes(by)) return { attribution: 'system', byName: '', atMs };
  const byName = trim(facts.archivedByName);
  if (byName) return { attribution: 'person', byName, atMs };
  return { attribution: 'unknown', byName: '', atMs };
}

export interface ClosureLadderInput {
  threadId: string;
  /**
   * Thread ids the viewer's LIVE index holds. That query is
   * `archived == false` server-side, so presence PROVES the thread is open.
   * Absence proves nothing — a thread also leaves the snapshot through
   * working-set churn (chatThreadIndexRepository's own note: churn "must NOT
   * close the user's active conversation") — so absence is `unknown`, never
   * `retired`.
   */
  liveThreadIds: ReadonlySet<string>;
  /** The durable archive tombstone map: presence proves retired, zero reads. */
  tombstoned: boolean;
  /** Facts already known for this thread, from the archived listener or a probe. */
  facts?: ThreadClosureFacts | null;
}

/**
 * The ladder, cheapest first. Every step fails toward `live`/`unknown` rather
 * than toward `retired`, deliberately: the archive fanout skips a member whose
 * index doc does not exist yet, so a fail-closed reading would grey a
 * freshly-invited member's row while their thread is perfectly live — which
 * destroys the feature instead of delivering it.
 */
export function resolveClosureState(input: ClosureLadderInput): ThreadClosureState {
  const id = trim(input.threadId);
  if (!id) return 'unknown';
  if (input.liveThreadIds.has(id)) return 'live';
  if (input.tombstoned) return 'retired';
  if (input.facts?.archived === true) return 'retired';
  if (input.facts && input.facts.archived === false) return 'live';
  return 'unknown';
}

/**
 * Most single-doc reads one pass may spend resolving `unknown` threads.
 *
 * Bounded rather than unbounded for a measured reason: a real account in this
 * repo's own archive test carried 131 archived threads of 223
 * (useChatThreads.archivedThreadLeavesTheRail.test.ts), so "read them all" is
 * not a size anyone should pay per pass. The ladder's first three tiers answer
 * nearly every row for free, so this cap is a belt, not the mechanism.
 */
export const CLOSURE_PROBE_CAP = 12;

/**
 * How many times one thread's doc is re-read after a THROWN read.
 *
 * Small on purpose: the failure this covers is transient (offline, a rules blip
 * mid-deploy), and a read that keeps throwing is answered correctly by
 * rendering the row open — which is what it looked like before this existed.
 * Lives here rather than in either consumer because BOTH the chat's hook and
 * the bell's store bound their retries, and two numbers for one concept is the
 * drift this module exists to prevent.
 */
export const MAX_CLOSURE_PROBE_ATTEMPTS = 3;

export interface ClosureProbePlan {
  /** Distinct thread ids, in the order given, that still need one doc read. */
  threadIds: string[];
  /** True when the cap truncated the plan — the rest resolve on a later pass. */
  truncated: boolean;
}

/**
 * Which threads to read a doc for: the DISTINCT ids nothing cheaper could
 * answer, minus the ones already answered or in flight, capped.
 *
 * Deliberately takes the ids in caller order (the list is newest-first), so the
 * rows a person is actually looking at are the ones a truncated pass resolves.
 */
export function planClosureProbes(args: {
  threadIds: readonly string[];
  liveThreadIds: ReadonlySet<string>;
  answered: ReadonlySet<string>;
  inFlight: ReadonlySet<string>;
  tombstoned?: (threadId: string) => boolean;
  cap?: number;
}): ClosureProbePlan {
  const cap = Number.isFinite(args.cap) && (args.cap as number) >= 0 ? Math.floor(args.cap as number) : CLOSURE_PROBE_CAP;
  const seen = new Set<string>();
  const out: string[] = [];
  let truncated = false;
  for (const raw of args.threadIds) {
    const id = trim(raw);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    if (args.liveThreadIds.has(id)) continue;
    if (args.answered.has(id)) continue;
    if (args.inFlight.has(id)) continue;
    if (args.tombstoned?.(id)) continue;
    if (out.length >= cap) { truncated = true; continue; }
    out.push(id);
  }
  return { threadIds: out, truncated };
}
